import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";

export default styled(Button)(({ theme, ownerState }) => {
  const { palette, functions, borders } = theme;
  const { color, variant, size, circular, iconOnly } = ownerState;

  const { white, dark, text, transparent, gradients } = palette;
  const { boxShadow, linearGradient, pxToRem, rgba } = functions;
  const { borderRadius } = borders;

  // styles for the button with variant="gradient"
  const containedStyles = () => {
    const backgroundValue = palette[color] ? palette[color].main : white.main;
    const focusedBackgroundValue = palette[color] ? palette[color].focus : white.focus;
    const boxShadowValue = palette[color]
      ? boxShadow([0, 0], [0, 3.2], palette[color].main, 0.5)
      : boxShadow([0, 0], [0, 3.2], dark.main, 0.5);

    let colorValue = white.main;
    if (color === "white" || !palette[color]) colorValue = text.main;
    else if (color === "light") colorValue = gradients.dark.state;

    let focusedColorValue = white.main;
    if (color === "white") focusedColorValue = text.main;
    else if (color === "primary" || color === "error" || color === "dark") focusedColorValue = white.main;

    return {
      background: backgroundValue,
      color: colorValue,
      opacity: 0.8, // Reduce opacity for glassmorphism
      backdropFilter: "blur(8px)", // Add the blur effect for frosted glass
      borderRadius: "5px", // Rounded corners for the frosted glass effect
      border: `1px solid ${rgba(white.main, 0.1)}`, // Subtle border to enhance glass look
      boxShadow: boxShadowValue,

      "&:hover": {
        backgroundColor: backgroundValue,
      },

      "&:focus:not(:hover)": {
        backgroundColor: focusedBackgroundValue,
        boxShadow: boxShadowValue,
      },

      "&:disabled": {
        backgroundColor: backgroundValue,
        color: focusedColorValue,
      },
    };
  };

  // styles for the button with variant="outlined"
  const outliedStyles = () => {
    const backgroundValue = color === "white" ? rgba(white.main, 0.1) : transparent.main;
    const colorValue = palette[color] ? palette[color].main : white.main;
    const boxShadowValue = palette[color]
      ? boxShadow([0, 0], [0, 3.2], palette[color].main, 0.5)
      : boxShadow([0, 0], [0, 3.2], white.main, 0.5);
    let borderColorValue = palette[color] ? palette[color].main : rgba(white.main, 0.75);

    return {
      background: backgroundValue,
      color: colorValue,
      borderColor: borderColorValue,
      opacity: 0.8, // Apply glassmorphism opacity
      backdropFilter: "blur(8px)", // Blur effect for frosted glass
      borderRadius: "5px", // Rounded corners for the glass effect

      "&:hover": {
        background: transparent.main,
        borderColor: colorValue,
      },

      "&:focus:not(:hover)": {
        background: transparent.main,
        boxShadow: boxShadowValue,
      },

      "&:active:not(:hover)": {
        backgroundColor: colorValue,
        color: white.main,
        opacity: 0.85,
      },

      "&:disabled": {
        color: colorValue,
        borderColor: colorValue,
      },
    };
  };

  // styles for the button with variant="gradient"
  const gradientStyles = () => {
    const backgroundValue = color === "white" || !gradients[color]
      ? white.main
      : linearGradient(gradients[color].main, gradients[color].state);

    let colorValue = white.main;
    if (color === "white") colorValue = text.main;
    else if (color === "light") colorValue = gradients.dark.state;

    return {
      background: backgroundValue,
      color: colorValue,
      opacity: 0.8, // Apply glassmorphism opacity
      backdropFilter: "blur(8px)", // Blur effect for frosted glass
      borderRadius: "5px", // Rounded corners for the glass effect

      "&:focus:not(:hover)": {
        boxShadow: "none",
      },

      "&:disabled": {
        background: backgroundValue,
        color: colorValue,
      },
    };
  };

  // styles for the button with variant="text"
  const textStyles = () => {
    const colorValue = palette[color] ? palette[color].main : white.main;
    const focusedColorValue = palette[color] ? palette[color].focus : white.focus;

    return {
      color: colorValue,
      opacity: 0.8, // Apply glassmorphism opacity
      backdropFilter: "blur(8px)", // Blur effect for frosted glass
      borderRadius: "5px", // Rounded corners for the glass effect

      "&:hover": {
        color: focusedColorValue,
      },

      "&:focus:not(:hover)": {
        color: focusedColorValue,
      },
    };
  };

  // styles for the button with circular={true}
  const circularStyles = () => ({
    borderRadius: "50%", // Full circle for the button
  });

  // styles for the button with iconOnly={true}
  const iconOnlyStyles = () => {
    let sizeValue = pxToRem(38);
    if (size === "small") sizeValue = pxToRem(25.4);
    else if (size === "large") sizeValue = pxToRem(52);

    let paddingValue = `${pxToRem(11)} ${pxToRem(11)} ${pxToRem(10)}`;
    if (size === "small") paddingValue = pxToRem(4.5);
    else if (size === "large") paddingValue = pxToRem(16);

    return {
      width: sizeValue,
      minWidth: sizeValue,
      height: sizeValue,
      minHeight: sizeValue,
      padding: paddingValue,
      opacity: 0.8, // Apply glassmorphism opacity
      backdropFilter: "blur(8px)", // Blur effect for frosted glass
      borderRadius: "5px", // Rounded corners for the glass effect

      "& .material-icons": {
        marginTop: 0,
      },

      "&:hover, &:focus, &:active": {
        transform: "none",
      },
    };
  };

  return {
    ...(variant === "contained" && containedStyles()),
    ...(variant === "outlined" && outliedStyles()),
    ...(variant === "gradient" && gradientStyles()),
    ...(variant === "text" && textStyles()),
    ...(circular && circularStyles()),
    ...(iconOnly && iconOnlyStyles()),
  };
});
